import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikErrors, FormikTouched } from 'formik';
import classnames from 'classnames';
import styled from 'styled-components';
import { Form, CheckboxProps } from 'semantic-ui-react';
import { Turnstile } from '@marsidev/react-turnstile';

import { isProduction } from '@/settings/environment';
import { CapyCaptcha, SetCapyValues } from '@/components/shared/CapyCaptcha';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';
import { Spinner } from '@/components/shared/Spinner';

export type Values = {
  email: string;
  ignoreTurnstile: boolean;
  ignoreCapyCaptcha: boolean;
};

type Props = {
  errors: FormikErrors<Values>;
  handleChange: ((e: React.ChangeEvent<HTMLInputElement>) => void) &
    ((event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void);
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isDisabled: boolean;
  touched: FormikTouched<Values>;
  values: Values;
  capyError: boolean;
  setCapyValues: SetCapyValues;
  useTurnstile: boolean;
  turnstileError: string;
  onTurnstileSuccess: (token: string) => void;
  onTurnstileError: () => void;
  featureFlagLoading: boolean;
};

export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  isDisabled,
  touched,
  values,
  capyError,
  setCapyValues,
  useTurnstile,
  turnstileError,
  onTurnstileSuccess,
  onTurnstileError,
  featureFlagLoading,
}) => {
  const { t, i18n } = useTranslation();

  // Feature Flag読み込み中はSpinnerを表示
  // APIレスポンス前にCapy/Turnstileが表示されて画面がチラつくのを防ぐ
  if (featureFlagLoading) {
    return (
      <Styled>
        <div className="loader-container">
          <Spinner />
        </div>
      </Styled>
    );
  }

  return (
    <Styled>
      <form onSubmit={handleSubmit}>
        <div className="email-input">
          <Form.Input
            data-test="email-field"
            icon="mail"
            iconPosition="left"
            label={t('ui.email.label')}
            className={classnames({ error: errors.email && touched.email })}
            name="email"
            onChange={handleChange}
            placeholder={t('ui.email.placeholder')}
            value={values.email}
          />
          {touched.email && errors.email && (
            <div>
              <Label>{errors.email}</Label>
            </div>
          )}
        </div>

        {/* Feature Flag による切り替え: Turnstile または Capy */}
        {useTurnstile ? (
          // Turnstileモード
          <div className="turnstile-wrapper">
            <Turnstile
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
              options={{
                language: i18n.language,
                theme: 'light',
              }}
              onSuccess={onTurnstileSuccess}
              onError={onTurnstileError}
            />
            {turnstileError && (
              <div className="turnstile-error" data-test="turnstile-error">
                <Label>{turnstileError}</Label>
              </div>
            )}
            {/* 開発環境のみスキップチェックボックスを表示 */}
            {!isProduction() && (
              <Form.Checkbox
                label={t('ui.turnstile.ignore')}
                id="ignoreTurnstile"
                name="ignoreTurnstile"
                data-test="ignore-turnstile"
                onChange={handleChange}
              />
            )}
          </div>
        ) : (
          // Capyモード
          <div className="capy-wrapper">
            <div>
              <CapyCaptcha onChange={setCapyValues} />
              <div>{t('ui.capyCaptcha.description')}</div>
              {capyError && <Label>{t('ui.capyCaptcha.error')}</Label>}
            </div>
            {/* 開発環境のみスキップチェックボックスを表示 */}
            {!isProduction() && (
              <Form.Checkbox
                label={t('ui.capyCaptcha.ignore')}
                id="ignoreCapyCaptcha"
                name="ignoreCapyCaptcha"
                data-test="ignore-capy-captcha"
                onChange={handleChange}
              />
            )}
          </div>
        )}

        <div className="submit-button">
          <SharedButton dataTest="do-verify" disabled={isDisabled} type="submit">
            {t('ui.action.submit')}
          </SharedButton>
        </div>
      </form>
    </Styled>
  );
};

const Styled = styled.div`
  .verification-error {
    margin-top: 24px;
  }

  .turnstile-wrapper {
    margin: 8px auto 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    border-radius: 4px;
  }

  .turnstile-error {
    margin-top: 8px;
    text-align: center;
  }

  .capy-wrapper {
    margin: 24px auto 28px;
    text-align: center;
  }

  .submit-button {
    text-align: center;
  }

  .loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    padding: 2em;
  }
`;
